import { isBlank } from '@ember/utils';
import { htmlSafe } from '@ember/template';
import Ember from 'ember';
import DOMPurify from 'dompurify';
const handlebars = Ember.Handlebars;
const {
  Utils
} = handlebars;
const FORBID_TAGS = ['svg', 'MathML'];

// DOMPurify strips `target` attributes from anchor tags by default, because `target="_blank"` exposes a vulnerability.
// The vulnerability is fixed by using a `rel` value of `noopener noreferrer`.
// These configs allow `target` attributes to be maintained, and updates the `rel` attribute to secure the vulnerability.
// Relevant issue for DOMPurify with context: https://github.com/cure53/DOMPurify/issues/317
const ADD_ATTR = ['target'];
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// Public: Use this to determine if a given url is on a whitelist of safe URLs
//
// url - URL to test
//
// Returns url if safe to use, otherwise null

export function checkWhitelistedUrl(url) {
  if ([
  // shifts url is used when redirecting back after editing a location's address
  '/dashboard/shifts', '/appointments'].some(whitelistedURL => url?.startsWith(whitelistedURL))) {
    return url;
  }
  return null;
}

// Public: Returns a copy of the given string that has gone through HTML sanitization and
// marked as safe to be inserted directly as HTML into the DOM.
//
// string - A String to be wrapped as safe for HTML insertion.
//
// Returns a wrapped String suitable for passing to Handlebars.

// When you want to render user supplied html, you should do it inside an iframe. The isolation level provided by iframes cannot be
// matched by any other method.

// If the product design isn't compatible with the limitations imposed by iframes, you may use this function to remove dynamic
// content/scripts from the user supplied string. Please notify infosec@ when favor DOMPurify over iframes.

// A few gotchas you might want to keep in mind:
//  - the output of DOMPurify is not safe to insert into jQuery's .html() function.
//  - the output of DOMPurify is not safe on older browsers. All bets are off in terms of web security when using an older browser so it's probably not a big deal.
export function sanitizedHTMLString(string) {
  return htmlSafe(DOMPurify.sanitize(string, {
    FORBID_TAGS,
    ADD_ATTR
  }));
}

// Public: Use this to escape HTML in strings.
//
// string - A String containing unescaped characters.
//
// Returns a String with dangerous characters replaced.
export function escapeString(string) {
  return Utils.escapeExpression(string);
}

/**
 * Handles interpolating values in a tagged template literal. When using this
 * function, make sure you use the tagged template literal syntax:
 *
 *   html`RIGHT!`
 *
 * Not typical function calling syntax:
 *
 *   html(`WRONG!`)
 *
 * @example
 *
 *   // The values of `title` and `description` will be HTML-escaped.
 *
 *   ```js
 *   html`
 *     <h1>${title}</h1>
 *
 *     <p>${description}</p>
 *   `
 *   ```
 *
 * @param {Array<string>} fragments
 * @param {Array<*>} values
 * @returns {Handlebars.SafeString}
 */
export function html(fragments, ...values) {
  if (!Array.isArray(fragments)) {
    throw new TypeError('`html` called as a regular function; use like this instead: html`<b>${name}</b>`' // eslint-disable-line no-template-curly-in-string
    );
  }
  return htmlSafe(fragments.reduce((result, fragment, i) => result + escapeString(`${isBlank(values[i - 1]) ? '' : values[i - 1]}`) + fragment));
}

/**
 * Validates that a URL is syntactically safe and either:
 * - a relative path (e.g. "/dashboard"), or
 * - an absolute http/https URL that is same-origin, or
 * - in local development, an absolute http/https URL where both the current page
 *   and the target are localhost (localhost, 127.0.0.1, or ::1), any port.
 *
 * @param {string} input - The URL string to validate.
 * @returns {boolean} True if the URL is valid and safe, otherwise false.
 */
export function isRealUrl(input) {
  if (typeof input !== 'string') {
    return false;
  }
  const trimmed = input.trim();
  if (!trimmed || hasUnsafeChars(trimmed)) {
    return false;
  }
  if (trimmed.startsWith('//')) {
    return false;
  }
  try {
    const url = new URL(trimmed, window.location.origin);
    const protocol = url.protocol.toLowerCase();
    if (protocol !== 'http:' && protocol !== 'https:') {
      return false;
    }
    const isAbsolute = /^[A-Za-z][\w+.-]*:/.test(trimmed);
    if (isAbsolute) {
      const sameOrigin = url.origin === window.location.origin;
      const targetIsLocalhost = isLocalhostHost(url.hostname);
      const currentIsLocalhost = isLocalhostHost(window.location.hostname);
      if (!sameOrigin) {
        if (!(currentIsLocalhost && targetIsLocalhost)) {
          return false;
        }
      }
      return true;
    }
    if (trimmed.startsWith('/')) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
export function hasUnsafeChars(s) {
  for (let i = 0; i < s.length; i++) {
    const code = s.codePointAt(i);
    if (code !== undefined && (code <= 0x1f || code === 0x7f)) {
      return true;
    }
  }
  return /\s/.test(s);
}
function isLocalhostHost(host) {
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
}